/**
 * Document Divergence Status page (CR-2026-035).
 *
 * Shows all is_edited documents and their divergence acknowledgement status.
 * Provides drill-down into audit history so an auditor can see who acknowledged
 * what, when, and why — without touching git.
 */
import React, { useEffect, useState } from 'react';
import {
  Container,
  Table,
  Loader,
  Segment,
  Header,
  Label,
  Statistic,
  Accordion,
  Icon,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../api';

const statusConfig = {
  acknowledged: { color: 'blue', label: 'Acknowledged' },
  unacknowledged: { color: 'orange', label: 'Unacknowledged' },
};

const DocDivergence = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    fetchApi(`${getApiPath()}/@docs/divergence`)
      .then((json) => {
        setData(json.items || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Loader active inline="centered" content="Loading divergence data..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment color="red">Error: {error}</Segment>
      </Container>
    );
  }

  const ackCount = data.filter((d) => d.divergence_status === 'acknowledged').length;
  const unackCount = data.filter((d) => d.divergence_status === 'unacknowledged').length;

  return (
    <Container style={{ paddingTop: '2em' }}>
      <Helmet title="Document Divergence Status" />

      <div className="polyglot-page-header">
        <h1>Document Divergence Status</h1>
        <p className="subtitle">
          Documents where the database version may differ from git
        </p>
      </div>

      <Statistic.Group size="small" style={{ marginBottom: '2em' }}>
        <Statistic>
          <Statistic.Value>{data.length}</Statistic.Value>
          <Statistic.Label>Edited Docs</Statistic.Label>
        </Statistic>
        <Statistic color="blue">
          <Statistic.Value>{ackCount}</Statistic.Value>
          <Statistic.Label>Acknowledged</Statistic.Label>
        </Statistic>
        <Statistic color="orange">
          <Statistic.Value>{unackCount}</Statistic.Value>
          <Statistic.Label>Unacknowledged</Statistic.Label>
        </Statistic>
      </Statistic.Group>

      {data.length === 0 ? (
        <Segment>No documents with is_edited=true.</Segment>
      ) : (
        <Table celled selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>File Path</Table.HeaderCell>
              <Table.HeaderCell>Title</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Acknowledged By</Table.HeaderCell>
              <Table.HeaderCell>Acknowledged At</Table.HeaderCell>
              <Table.HeaderCell>Reason</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {data.map((doc) => {
              const cfg = statusConfig[doc.divergence_status] || statusConfig.unacknowledged;
              const isExpanded = expandedRow === doc.id;
              return (
                <React.Fragment key={doc.id}>
                  <Table.Row
                    onClick={() => setExpandedRow(isExpanded ? null : doc.id)}
                    style={{ cursor: 'pointer' }}
                    warning={doc.divergence_status === 'unacknowledged'}
                  >
                    <Table.Cell>
                      <Icon name={isExpanded ? 'caret down' : 'caret right'} />
                      <code style={{ fontSize: '0.85em' }}>{doc.file_path}</code>
                    </Table.Cell>
                    <Table.Cell>{doc.title}</Table.Cell>
                    <Table.Cell>
                      <Label color={cfg.color} size="small">
                        {cfg.label}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>{doc.divergence_ack_by || '\u2014'}</Table.Cell>
                    <Table.Cell>
                      {doc.divergence_ack_at
                        ? new Date(doc.divergence_ack_at).toLocaleString()
                        : '\u2014'}
                    </Table.Cell>
                    <Table.Cell>{doc.divergence_ack_reason || '\u2014'}</Table.Cell>
                  </Table.Row>
                  {isExpanded && (
                    <Table.Row>
                      <Table.Cell colSpan="6" style={{ background: '#f9f9f9' }}>
                        <AuditHistory
                          history={doc.audit_history}
                          dbGitHash={doc.db_git_hash}
                          ackHash={doc.divergence_ack_hash}
                        />
                      </Table.Cell>
                    </Table.Row>
                  )}
                </React.Fragment>
              );
            })}
          </Table.Body>
        </Table>
      )}
    </Container>
  );
};

const AuditHistory = ({ history, dbGitHash, ackHash }) => (
  <div>
    <Header as="h4" style={{ marginBottom: '0.5em' }}>
      Audit Trail
    </Header>
    <p style={{ fontSize: '0.85em', color: '#666' }}>
      DB git_hash: <code>{dbGitHash || 'none'}</code>
      {ackHash && (
        <>
          {' \u00B7 '}Acknowledged hash: <code>{ackHash}</code>
        </>
      )}
    </p>
    {(!history || history.length === 0) ? (
      <p style={{ fontStyle: 'italic', color: '#999' }}>
        No relevant audit events recorded.
      </p>
    ) : (
      <Table compact size="small" basic="very">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Timestamp</Table.HeaderCell>
            <Table.HeaderCell>Operation</Table.HeaderCell>
            <Table.HeaderCell>User</Table.HeaderCell>
            <Table.HeaderCell>Changed Fields</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {history.map((evt, i) => (
            <Table.Row key={i}>
              <Table.Cell>
                {new Date(evt.operation_timestamp).toLocaleString()}
              </Table.Cell>
              <Table.Cell>{evt.operation}</Table.Cell>
              <Table.Cell>{evt.operation_user}</Table.Cell>
              <Table.Cell>
                {(evt.changed_fields || []).join(', ') || '\u2014'}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    )}
  </div>
);

export default DocDivergence;
